import React, { useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'

import RateTableFooter from 'components/footer'
import WidgetPage from 'components/hocs/widget-page'
import { useViewport } from 'components/hooks'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import { getMetaQuery } from 'utils/data'
import withApolloProvider from 'components/hocs/withApolloProvider'

import VerticalTabsSection from './components/tabs-section'
import styles from './styles.module.scss'
import { getValidVerticals } from './components/tabs-section/utils'

const VARIATIONS = ['full', 'resizable', 'fixed-height']

const getAdvertiserDisclosure = (theme, widgetContext) => {
  // Defaults to true if error occurs finding showDisclosure property
  const showDisclosure = get(widgetContext, ['options', 'multiVertical', 'showDisclosure'], true)

  if (!showDisclosure) return null

  switch (theme) {
    case 'fox':
    case 'fox1':
      return <AdvertiserDisclosure theme={theme} tooltipId="multi-vertical-advertiser-disclosure" />
    default:
      return null
  }
}

const convertVerticals = (verticals) => {
  if (typeof verticals === 'string') {
    return verticals.split(',')
  }
  return verticals
}

const getTooltipOffSet = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { width } = useViewport()

  return width < 400 ? '{"bottom": -30}' : ''
}

const getTitles = (widgetContext, vertical) => {
  const titleText = get(
    widgetContext,
    ['options', 'multiVertical', 'titleText'],
    'Shop around. One marketplace.',
  )

  const defaultSubtitleText =
    vertical === 'high-yield-savings'
      ? 'Select savings options from our partners'
      : 'Select a product to compare rates'

  const subTitleText = get(
    widgetContext,
    ['options', 'multiVertical', 'subTitleText'],
    defaultSubtitleText,
  )

  return { titleText, subTitleText }
}

const getBackground = (theme, variation) =>
  theme === 'yahoo1' && variation !== 'full' ? 'yahoo-background' : ''

const RateTableApp = ({
  verticals,
  variation,
  theme,
  widgetContext,
  metaParams,
  type,
  ...props
}) => {
  const validVerticals = getValidVerticals(convertVerticals(verticals))
  const [vertical, setVertical] = useState(validVerticals[0])

  const cssCls = VARIATIONS.includes(variation) ? variation : ''
  const background = getBackground(theme, variation)
  const metaQuery = getMetaQuery({ ...metaParams, marketplace: verticals, type }, theme)

  const { titleText, subTitleText } = getTitles(widgetContext, vertical)

  return (
    <div className={[styles.outerContainer, styles[cssCls]].join(' ')}>
      {getAdvertiserDisclosure(theme, widgetContext)}
      <div className={[styles.contentContainer, styles[background], styles[theme]].join(' ')}>
        <p className={`${styles.title} heading bold mb-16 mb-sm-0`}>{titleText}</p>
        <p className={`${styles.title} bodySmall mb-16 hidden-xs`}>{subTitleText}</p>
        <VerticalTabsSection
          vertical={vertical}
          verticals={validVerticals}
          variation={variation}
          setVertical={setVertical}
          theme={theme}
          widgetContext={widgetContext}
          metaQuery={metaQuery}
          {...props}
        />
        <RateTableFooter theme={theme} noMargin multiVertical offSet={getTooltipOffSet()} />
      </div>
    </div>
  )
}

RateTableApp.propTypes = {
  verticals: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variation: PropTypes.string,
  theme: PropTypes.string,
  widgetContext: PropTypes.shape({
    options: PropTypes.shape({
      multiVertical: PropTypes.shape({
        showDisclosure: PropTypes.bool,
        titleText: PropTypes.string,
        subTitleText: PropTypes.string,
        defaultFilters: PropTypes.object,
      }),
    }),
  }),
  metaParams: PropTypes.object,
  type: PropTypes.string,
}

export { RateTableApp, getAdvertiserDisclosure }

export default WidgetPage(withApolloProvider(RateTableApp))
